import { z } from "zod";
import { GROUP_DEFAULT_ROLES, OVERRIDE_ROLES, ROLE_NAMES } from "../permissions";

/**
 * Access that follows the customer's Entra groups (group access, 2026-09-28; design
 * docs/research/2026-09-28-design-group-access.md, section 4.4).
 */

/**
 * The platform feature flag (`features.flags.group_mapping`, "Entra group mapping" in Platform
 * settings, Feature flags). Absent or false is OFF, the default: access is by direct grants only,
 * the server reads no groups claim and no mapping, the group routes answer 404, and the client
 * shows no group access anywhere (hidden by the owner on 2026-10-01; to turn it back on, see
 * docs/research/2026-10-01-parked-group-access.md). `GET /me` carries it as `features.groupAccess`.
 */
export const GROUP_MAPPING_FLAG = "group_mapping";

/** An Entra object id: a GUID, compared in lower case. */
export const ENTRA_OBJECT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const groupMappingParamsSchema = z.object({ id: z.uuid(), mappingId: z.uuid() });
export type GroupMappingParams = z.infer<typeof groupMappingParamsSchema>;

export const groupMemberParamsSchema = z.object({ userId: z.uuid() });
export type GroupMemberParams = z.infer<typeof groupMemberParamsSchema>;

/**
 * `POST /tenants/:id/group-mappings`. TenantAdmin as the default role maps the group to the whole
 * tenant; any other role maps it to each named instance of the tenant (one mapping per instance).
 */
export const createGroupMappingBodySchema = z
  .object({
    idpGroupId: z
      .string()
      .trim()
      .regex(ENTRA_OBJECT_ID, "Enter the group's Object ID from Entra ID, a GUID.")
      .transform((value) => value.toLowerCase()),
    label: z.string().trim().min(1).max(200),
    roleName: z.enum(GROUP_DEFAULT_ROLES),
    instanceIds: z.array(z.uuid()).max(100).optional(),
    reason: z.string().trim().min(3).max(500),
  })
  .superRefine((value, ctx) => {
    const instances = value.instanceIds ?? [];
    if (value.roleName === "TenantAdmin" && instances.length > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["instanceIds"],
        message: "A group with the tenant admin role covers the whole tenant. Name no instance.",
      });
    }
    if (value.roleName !== "TenantAdmin" && instances.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["instanceIds"],
        message: "Choose at least one instance the group reaches.",
      });
    }
  });
export type CreateGroupMappingBody = z.infer<typeof createGroupMappingBodySchema>;

export const removeGroupMappingBodySchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type RemoveGroupMappingBody = z.infer<typeof removeGroupMappingBodySchema>;

export const groupEnforcementBodySchema = z.object({
  enabled: z.boolean(),
  reason: z.string().trim().min(3).max(500),
});
export type GroupEnforcementBody = z.infer<typeof groupEnforcementBodySchema>;

export const groupOverrideBodySchema = z.object({
  roleName: z.enum(OVERRIDE_ROLES),
  reason: z.string().trim().max(500).optional(),
});
export type GroupOverrideBody = z.infer<typeof groupOverrideBodySchema>;

export const groupsClaimStateSchema = z.enum([
  "present",
  "missing",
  "overage_resolved",
  "overage_unresolved",
]);
export type GroupsClaimStateView = z.infer<typeof groupsClaimStateSchema>;

export const groupMappingViewSchema = z.object({
  id: z.uuid(),
  idpGroupId: z.string(),
  label: z.string(),
  roleName: z.enum(ROLE_NAMES),
  scopeType: z.enum(["tenant", "instance"]),
  instanceId: z.uuid().nullable(),
  instanceName: z.string().nullable(),
  reason: z.string().nullable(),
  /** Open enrolments (`user_group_access` rows not ended). */
  people: z.number().int(),
  /** The latest sign-in any member was seen through this mapping. */
  lastSeenAt: z.string().nullable(),
  createdAt: z.string(),
});
export type GroupMappingView = z.infer<typeof groupMappingViewSchema>;

/**
 * Why the signed-in admin may not turn "Require a directory group" on, if they may not. The UI
 * shows the matching sentence; the server refuses with the same one.
 */
export const enforcementBlockSchema = z.enum([
  "no_mapping",
  "not_work_account",
  "groups_claim_missing",
  "groups_overage",
  "not_in_group",
]);
export type EnforcementBlock = z.infer<typeof enforcementBlockSchema>;

export const ENFORCEMENT_BLOCK_MESSAGES: Record<EnforcementBlock, string> = {
  no_mapping: "Add a directory group first.",
  not_work_account: "Sign in with your organisation's Microsoft account to turn this on.",
  groups_claim_missing:
    "Your sign-in carries no groups claim, so it cannot show you are in one of these groups. Ask your infrastructure team to add the groups claim to the skyData API app registration.",
  groups_overage:
    "Your sign-in lists too many groups to read, so it cannot show you are in one of these groups. Assign the group to the skyData enterprise application, or ask Skypoint to turn on the Microsoft Graph lookup.",
  not_in_group: "You are not in any of this tenant's groups. Turning this on would lock you out.",
};

export const groupMappingsResponseSchema = z.object({
  tenantId: z.uuid(),
  /** The customer's Entra tenant id; a mapping cannot be added without it. */
  entraTenantId: z.string().nullable(),
  enforcement: z.object({
    enabled: z.boolean(),
    changedAt: z.string().nullable(),
  }),
  mappings: z.array(groupMappingViewSchema),
  /**
   * People who sign in with a work account, hold access in this tenant, and are in none of its
   * groups by the record: who "Require a directory group" would lock out today.
   */
  outsideGroups: z.number().int(),
  /** People of this tenant whose last token had more groups than could be read. */
  overageUsers: z.number().int(),
  /** The signed-in admin's own token, for the guard and the setup warnings. */
  caller: z.object({
    groupsClaim: groupsClaimStateSchema.nullable(),
    workAccount: z.boolean(),
    inTenantGroup: z.boolean(),
    exempt: z.boolean(),
    enforceBlockedBy: enforcementBlockSchema.nullable(),
  }),
});
export type GroupMappingsResponse = z.infer<typeof groupMappingsResponseSchema>;

export const groupMemberViewSchema = z.object({
  userId: z.uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
});
export type GroupMemberView = z.infer<typeof groupMemberViewSchema>;
